import "./PerfilesTable.css";
import React, { useState, useEffect, useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { IoIosAddCircle } from "react-icons/io";
// import { Accordion } from "react-bootstrap"; // removed
import Loading from "../0. Utilidades/loadingSpinner";

function normalizeString(val) {
  if (val == null) return "";
  return String(val)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function PerfilesTable({
  apiUrl,
  columns,
  onRowClick,
  category,
  title, 
  initialPageIndex = 0,
  onPageChange,
  pageSize: externalPageSize = 10,
  onPageSizeChange,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [comunaFilter, setComunaFilter] = useState("");
  const [oficioFilter, setOficioFilter] = useState("");
  const [extraFilter, setExtraFilter] = useState("");
  const categoryLabel = title || category.charAt(0).toUpperCase() + category.slice(1);
  const isMaestras = category === "maestras";
  const extraFilterLabel = isMaestras ? "Maestras" : "Empresa";
  const extraFilterKey = isMaestras ? "sec" : "empresa";

  useEffect(() => {
    setIsLoading(true);
    const base = window.location.origin;
    const url = new URL(apiUrl, base);
    if (comunaFilter) url.searchParams.append("comuna", comunaFilter);
    if (oficioFilter) url.searchParams.append("oficio", oficioFilter);
    if (extraFilter) url.searchParams.append(extraFilterKey, extraFilter);

    fetch(url.toString())
      .then((response) => response.json())
      .then((data) => {
        setData(data);
        setIsLoading(false);
      });
  }, [apiUrl, comunaFilter, oficioFilter, extraFilter, extraFilterKey]);

  const filteredData = useMemo(() => {
    const normalizedSearchTerm = normalizeString(searchQuery);
    return data.filter((item) => {
      return columns.some((column) =>
        normalizeString(item[column.accessor]).includes(normalizedSearchTerm),
      );
    });
  }, [data, searchQuery, columns]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: initialPageIndex, pageSize: externalPageSize },
    },
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    onPageChange?.(pageIndex);
  }, [pageIndex, onPageChange]);

  useEffect(() => {
    onPageSizeChange?.(pageSize);
  }, [pageSize, onPageSizeChange]);

  const isEmptyCategory =
    !isLoading &&
    Array.isArray(data) &&
    data.length === 0 &&
    !comunaFilter &&
    !oficioFilter &&
    !extraFilter &&
    !searchQuery;

  if (isEmptyCategory) {
    return (
      <>
      <div className="generic-table-header">
        <h5>
          Buscador de {categoryLabel}
        </h5>
      </div>
      <div className="no-data-square">
        <h5>Sin información.</h5>
        <p>
          No hay datos para esta categoría: <strong>{categoryLabel}</strong>.
        </p>
      </div>
      </>
    );
  }

  return (
    <>
      <div className="generic-table-header">
        <h5>
          Buscador de {categoryLabel}
        </h5>
      </div>

      <div className="generic-table-wrapper">
        <a
          href={`/api/perfiles/anadir_${category}/`}
          className="btn btn-secondary mb-3 add-item-button"
        >
          Agregar Nuevo <IoIosAddCircle />
        </a>

        <div className="filter-controls d-flex gap-2">
          <select
            className="form-select"
            value={comunaFilter}
            onChange={(e) => setComunaFilter(e.target.value)}
          >
            <option value="">Todas las Comunas</option>
            {[...new Set(data.map((d) => d.comuna))]
              .sort((a, b) =>
                a.localeCompare(b, "es", { sensitivity: "base" }),
              )
              .map((comuna) => (
                <option key={comuna} value={comuna}>
                  {comuna}
                </option>
              ))}
          </select>

          <select
            className="form-select"
            value={oficioFilter}
            onChange={(e) => setOficioFilter(e.target.value)}
          >
            <option value="">Todos los Oficios</option>
            {[...new Set(data.map((d) => d.oficio))]
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }))
              .map((oficio) => (
                <option key={oficio} value={oficio}>
                  {oficio}
                </option>
              ))}
          </select>

          <select
            className="form-select"
            value={extraFilter}
            onChange={(e) => setExtraFilter(e.target.value)}
          >
            <option value="">{`Todas las ${extraFilterLabel}`}</option>

            {isMaestras ? (
              <>
                <option value="true">Certificadas SEC</option>
                <option value="false">Sin Certificación</option>
              </>
            ) : (
              [...new Set(data.map((d) => d[extraFilterKey]))]
                .filter(Boolean)
                .sort((a, b) =>
                  a.localeCompare(b, "es", { sensitivity: "base" }),
                )
                .map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))
            )}
          </select>
        </div>

        <button
          className="filter-controls btn btn-outline-primary"
          onClick={() => {
            setComunaFilter("");
            setOficioFilter("");
            setExtraFilter(""); // ✅ correctly clears the filter
          }}
        >
          Limpiar Filtros
        </button>

        <div className="generic-table">
          <div className="generic-search-bar">
            <input
              type="text"
              className="form-control"
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="clear-search-button"
                onClick={() => setSearchQuery("")}
              >
                &times;
              </button>
            )}
          </div>

          {isLoading ? (
            <Loading />
          ) : (
            <>
              <div className="generic-pagination">
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 20, 30, 40, 50].map((size) => (
                    <option key={size} value={size}>
                      Mostrar {size}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                >
                  {"<"}
                </button>
                <span>
                  {" "}
                  Página {pageIndex + 1} de {pageOptions.length}{" "}
                </span>
                <button onClick={() => nextPage()} disabled={!canNextPage}>
                  {">"}
                </button>
              </div>
              <table {...getTableProps()} className="table table-striped">
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps(),
                          )}
                        >
                          {column.render("Header")}
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? " 🔽"
                                : " 🔼"
                              : ""}
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        onClick={() => onRowClick(row.original)}
                      >
                        {row.cells.map((cell) => {
                          return (
                            <td {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default PerfilesTable;
